import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { ENDPOINTS } from "../Services/ServiceConfig";

// Feature flag: Enable static file fetching (set to true when static files are deployed)
// When false, goes directly to API without 404 latency from missing files
const ENABLE_STATIC_FILE_FETCH = false;

// Get API root URL (remove /api/ suffix for static files)
const getStaticFileBaseUrl = () => {
  const apiUrl = ENDPOINTS.BASE_URL || "";
  return apiUrl.replace(/\/api\/?$/, "");
};

// Check if request has any filters applied
const hasFilters = (body: any): boolean => {
  if (!body) return false;
  const filterKeys = [
    "tenantId", "tenantName", "region", "segmentGroup", "segment", 
    "subSegment", "isS500", "industry", "agreementEndQuarter",
    "overall_CustomerHealthScoreStatus", "overall_SupportHealthScoreStatus",
    "e5RenewalTimeframe", "initiatives", "subInitiatives", "productFamily"
  ];
  return filterKeys.some(key => body[key] && body[key].trim() !== "");
};

/**
 * Explore Health API.
 *
 * Data Flow for 360 View (default, no filters):
 * - ENABLE_STATIC_FILE_FETCH=true: Static JSON file first (~50ms), then API fallback
 * - ENABLE_STATIC_FILE_FETCH=false: API directly (Redis → Kusto)
 *
 * Admin generates static files via POST /api/admin/staticData/exploreHealth360/generate
 */

// Define a service using a base URL and expected endpoints
export const exploreHealthAPI = createApi({
  reducerPath: "exploreHealth",
  baseQuery: baseQueryWithReauth,
  // Cache settings for optimal performance
  keepUnusedDataFor: 300, // 5 minutes
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    getHealthFilterPillData: builder.query({
      query: () => "health/getHealthFilterPillData",
      keepUnusedDataFor: 600 // 10 minutes for filter options
    }),
    getHealthData: builder.mutation<any, any>({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        // For default view (no filters), try static file first if enabled
        if (ENABLE_STATIC_FILE_FETCH && !hasFilters(body)) {
          try {
            const staticFileUrl = `${getStaticFileBaseUrl()}/data/exploreHealth/health-data-default.json`;
            const response = await fetch(staticFileUrl);
            if (response.ok) {
              const data = await response.json();
              return { data };
            }
          } catch {
            // Static file failed, fall through to API
          }
        }
        
        // Filters applied or static file not available/disabled - use API
        const result = await baseQuery({
          url: "health/getHealthData",
          method: "POST",
          body
        });
        
        if (result.error) {
          return { error: result.error };
        }
        
        return { data: result.data };
      }
    }),
    getHealthDataByTPID: builder.mutation<any, any>({
      query(body) {
        return {
          url: "health/getHealthDataByTPID",
          method: "POST",
          body
        };
      }
    }),
    getHealthHeatMapData: builder.query({
      // Accepts [tenantId, productFamily] as argument
      query: ([tenantId, productFamily]: [string, string]) =>
        `health/getHealthHeatMapData?tenantId=${tenantId}&productFamily=${productFamily}`,
      keepUnusedDataFor: 300
    }),
    getCustomerHealthData: builder.query({
      // Accepts [tenantId, productFamily] as argument
      query: ([tenantId, productFamily]: [string, string]) =>
        `health/getCustomerHealthData?tenantId=${tenantId}&productFamily=${productFamily}`,
      keepUnusedDataFor: 300
    }),
    getCustomerHealthProductTrendData: builder.query({
      // Accepts [tenantId, productFamily] as argument
      query: ([tenantId, productFamily, numberOfMonths]: [string, string, number]) =>
        `health/getCustomerHealthProductTrendData?tenantId=${tenantId}&productFamily=${productFamily}&numberOfMonths=${numberOfMonths}`,
      keepUnusedDataFor: 300
    }),
    getTPIDCustomerHealthData: builder.query({
      query: ([topIdentifierID, productFamily]: [string, string]) =>
        `health/getTPIDCustomerHealthData?topIdentifierID=${topIdentifierID}&productFamily=${productFamily}`,
      keepUnusedDataFor: 300
    }),
    getTPIDHealthHeatMapData: builder.query({
      query: ([topIdentifierID, productFamily]: [string, string]) =>
        `health/getTPIDHealthHeatMapData?topIdentifierID=${topIdentifierID}&productFamily=${productFamily}`,
      keepUnusedDataFor: 300
    }),
    getTPIDCustomerHealthProductTrendData: builder.query({
      query: ([topIdentifierID, productFamily, numberOfMonths]: [string, string, number]) =>
        `health/getTPIDCustomerHealthProductTrendData?topIdentifierID=${topIdentifierID}&productFamily=${productFamily}&numberOfMonths=${numberOfMonths}`,
      keepUnusedDataFor: 300
    })
  })
});

export const HEALTH_DATA_TPID_CACHE_KEY = "getHealthDataByTPID";

// Export hooks for usage in functional components, which are
// Auto-generated based on the defined endpoints
export const {
  useGetHealthFilterPillDataQuery,
  useGetHealthDataMutation,
  useGetHealthDataByTPIDMutation,
  useGetHealthHeatMapDataQuery,
  useGetCustomerHealthDataQuery,
  useGetCustomerHealthProductTrendDataQuery,
  useGetTPIDCustomerHealthDataQuery,
  useGetTPIDHealthHeatMapDataQuery,
  useGetTPIDCustomerHealthProductTrendDataQuery
} = exploreHealthAPI;
